import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RestPoliceService} from '@app/core/api/police.service';
import {ActivePoliceQuery} from '@app/core/state/active-police/active-police.query';
import {ActivePoliceState, ActivePoliceStore} from '@app/core/state/active-police/active-police.store';
import {PayloadUtil} from '@app/core/util/payload.util';
import {Klausel, Polizze, Position, PositionResult, Vertrag, VertragResult} from '@pezi/entities';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {setProps} from "@ngneat/elf";

@Injectable({providedIn: 'root'})
export class ActivePoliceService {

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private activePoliceStore: ActivePoliceStore,
              public activePoliceQuery: ActivePoliceQuery,
              private policeService: RestPoliceService,
              private snackBar: MatSnackBar) {
  }

  initPolice(policeId: number, verId?: number, posId?: number) {
    this.destroy$.next(true);
    this.activePoliceStore.data.update(setProps({
      policeLoading: false, vertraegeLoading: false, policeLoadingError: false
    }));

    const currentState = this.activePoliceStore.data.getValue();
    const policeChanged = !currentState.police || currentState.police.id !== +policeId;
    const vertragChanged = !currentState.vertrag || currentState.vertrag.id !== +verId;
    if (!policeId) {
      this.resetPoliceData();
    } else {
      if (policeChanged) {
        this.resetPoliceData();
        this.initPoliceData(policeId);
      }
      if (!verId) {
        this.resetVertragData();
      } else {
        this.handleVertragPositionen(currentState, policeChanged, vertragChanged, policeId, verId, posId)
      }
    }
  }

  clearStore() {
    this.activePoliceStore.data.reset();
  }

  private handleVertragPositionen(currentState: ActivePoliceState, policeChanged: boolean, vertragChanged: boolean, policeId: number, verId: number, posId?: number): void {
    if (policeChanged || vertragChanged) {
      this.resetVertragData();
      this.initPolizzenVertrag(policeId, verId);
      this.initAvbVertrag(policeId, verId);
      this.initKlauselnVertrag(policeId, verId);
      this.initPositionenVertrag(policeId, verId);
    }
    if (!posId) {
      this.resetPositionData();
    } else {
      if (policeChanged || vertragChanged || !currentState.position || currentState.position.id !== +posId) {
        this.resetPositionData();
        this.initPosition(policeId, verId, posId);
        this.initKlauselnPosition(policeId, verId, posId);
      }
    }
  }

  private initVertraegeForPolice(policeId: number) {
    this.activePoliceStore.data.update(setProps({
      vertraegeLoading: true
    }));
    this.policeService.getVertraegeForPolice(policeId)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      ).subscribe(result => {
      let vertraege: VertragResult[] = [];
      if (PayloadUtil.handlePayloadSnackbarErrorOnly(result, this.snackBar,
        'Fehler: Verträge konnten nicht geladen werden')) {
        vertraege = result.data;
      }
      this.activePoliceStore.data.update(setProps({
        vertraegeLoading: false, vertraege
      }));
    });
  }

  private initPoliceData(policeId: number) {
    this.activePoliceStore.data.update(setProps({
      policeLoading: true
    }));
    this.policeService.getPoliceForId(policeId)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        let police: Polizze;
        let error = false;
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(result, this.snackBar,
          'Fehler: Polizze konnte nicht geladen werden')) {
          police = result.data;
        } else {
          error = true;
        }
        this.activePoliceStore.data.update(setProps({
          policeLoading: false, police, policeLoadingError: error
        }));
        if (this.activePoliceStore.data.getValue().police && !this.activePoliceStore.data.getValue().police.locked) {
          this.initVertraegeForPolice(policeId);
        }
      });
  }

  private initPolizzenVertrag(polint: number, vernr: number): void {
    this.activePoliceStore.data.update(setProps({
      vertragLoading: true
    }));
    this.policeService.getVertragForPolice(polint, vernr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let vertrag: Vertrag;
        let error = false;
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: Vertrag konnte nicht geladen werden')) {
          vertrag = payload.data;
        } else {
          error = true;
        }
        this.activePoliceStore.data.update(setProps({
          vertragLoading: false, vertrag, vertragLoadingError: error
        }));
      });
  }

  private initKlauselnVertrag(polint: number, vernr: number): void {
    this.activePoliceStore.data.update(setProps({
      vertragKlauselnLoading: true
    }));
    this.policeService.getKlauselnForVertrag(polint, vernr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let klauseln: Klausel[] = [];
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: Klauseln konnten nicht geladen werden')) {
          klauseln = payload.data;
        }
        this.activePoliceStore.data.update(setProps({
          vertragKlauselnLoading: false, vertragKlauseln: klauseln
        }));
      });
  }

  private initAvbVertrag(polint: number, vernr: number): void {
    this.activePoliceStore.data.update(setProps({
      avbsLoading: true
    }));
    this.policeService.getAvbForVertrag(polint, vernr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let avbs: Klausel[] = [];
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: AVBs konnten nicht geladen werden')) {
          avbs = payload.data;

        }
        this.activePoliceStore.data.update(setProps({
          avbsLoading: false, avbs
        }));
      });
  }

  private initPositionenVertrag(polint: number, vernr: number): void {
    this.activePoliceStore.data.update(setProps({
      positionenLoading: true
    }));
    this.policeService.getPositionenForVertraege(polint, vernr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let positionen: PositionResult[] = [];
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: Positionen konnten nicht geladen werden')) {
          positionen = payload.data;

        }
        this.activePoliceStore.data.update(setProps({
          positionenLoading: false, positionen
        }));
      });
  }

  private initPosition(polint: number, vernr: number, posnr: number): void {
    this.activePoliceStore.data.update(setProps({
      positionLoading: true
    }));
    this.policeService.getPositionForVertraege(polint, vernr, posnr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let position: Position;
        let error = false;
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: Position konnte nicht geladen werden')) {
          position = payload.data;
        } else {
          error = true;
        }
        this.activePoliceStore.data.update(setProps({
          positionLoading: false, position, positionLoadingError: error
        }));
      });
  }

  private initKlauselnPosition(polint: number, vernr: number, posnr: number): void {
    this.activePoliceStore.data.update(setProps({
      positionKlauselnLoading: true
    }));
    this.policeService.getKlauselnForPosition(polint, vernr, posnr)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        let klauseln: Klausel[] = [];
        if (PayloadUtil.handlePayloadSnackbarErrorOnly(payload, this.snackBar,
          'Fehler: Klauseln konnten nicht geladen werden')) {
          klauseln = payload.data;

        }
        this.activePoliceStore.data.update(setProps({
          positionKlauselnLoading: false, positionKlauseln: klauseln
        }));
      });
  }

  /**
   * löscht alle Daten von police abwärts
   * Wenn sich die polizze ändert, können vertrag und position sowieso nicht mehr stimmen
   *
   * @private
   */
  private resetPoliceData() {
    this.activePoliceStore.data.reset();
  }

  /**
   * löscht alle Daten von vertrag abwärts
   * Wenn sich der Vertrag ändert, kann position sowieso nicht mehr stimmen
   *
   * @private
   */
  private resetVertragData() {
    this.activePoliceStore.data.update(setProps({
      vertragLoading: false,
      vertragLoadingError: false,
      vertrag: undefined,
      vertragKlauseln: [],
      vertragKlauselnLoading: false,
      avbsLoading: false,
      avbs: [],
      positionenLoading: false,
      positionen: []
    }));
    this.resetPositionData();
  }

  /**
   * löscht alle Position Daten
   *
   * @private
   */
  private resetPositionData() {
    this.activePoliceStore.data.update(setProps({
      positionLoading: false,
      positionLoadingError: false,
      position: undefined,
      positionKlauselnLoading: false,
      positionKlauseln: []
    }));
  }
}
